import React from "react";
import { FiArrowLeft, FiUser } from "react-icons/fi";
import {
  formatDateKey,
  formatHours,
  formatShortDateLabel,
  formatWeekdayLabel,
  getDailyHoursTotals,
  TIMESHEET_VIEW,
} from "../../../utils/timesheetUtils";

const EmployeeTimesheetDetail = ({
  detail,
  days,
  viewMode,
  loading,
  errorMessage,
  onBack,
}) => {
  if (loading) {
    return (
      <div className="rounded-3xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-14 text-center shadow-sm">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          กำลังโหลดข้อมูลเวลาทำงานของพนักงาน...
        </p>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="rounded-3xl border border-red-200 dark:border-red-900/50 bg-red-50/80 dark:bg-red-500/10 px-6 py-10 shadow-sm">
        <p className="text-sm font-medium text-red-700 dark:text-red-300">
          {errorMessage}
        </p>
      </div>
    );
  }

  if (!detail?.user) return null;

  const dailyTotals = getDailyHoursTotals(detail.clients, days);
  const totalHours = Object.values(dailyTotals).reduce(
    (sum, hours) => sum + hours,
    0
  );

  const hasData = (detail.clients || []).length > 0;

  if (!hasData) {
    return (
      <div className="rounded-3xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300">
              <FiUser size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 break-all">
                {detail.user.username}
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {detail.user.department || "-"} • ชั่วโมงรวม {formatHours(detail.totalHours)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-indigo-300 dark:hover:border-indigo-700"
          >
            <FiArrowLeft />
            ปิด
          </button>
        </div>

        <div className="mt-6 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/30 px-6 py-12 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            ยังไม่มีรายการเวลาทำงานของพนักงานคนนี้ในช่วงเวลาที่เลือก
          </p>
        </div>
      </div>
    );
  }

  if (viewMode === TIMESHEET_VIEW.MONTH) {
    return (
      <div className="rounded-3xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300">
              <FiUser size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 break-all">
                {detail.user.username}
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {detail.user.department || "-"} • ชั่วโมงรวม {formatHours(detail.totalHours)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-indigo-300 dark:hover:border-indigo-700"
          >
            <FiArrowLeft />
            ปิด
          </button>
        </div>

        <div className="mt-6 overflow-hidden rounded-3xl border border-gray-200 dark:border-gray-700">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/60">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">
                    ลูกค้า / Project
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-200">
                    ชั่วโมงรวม
                  </th>
                </tr>
              </thead>
              <tbody>
                {detail.clients.map((client) => (
                  <React.Fragment key={client.clientId}>
                    <tr className="border-t border-gray-100 bg-indigo-100 dark:border-gray-700/80 dark:bg-[#3a447b]">
                      <td className="px-4 py-3 font-semibold text-gray-900 dark:text-gray-100">
                        {client.clientName}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-gray-100">
                        <span className="inline-flex min-w-9 items-center justify-center rounded-full bg-indigo-200/80 px-3 py-1 text-indigo-950 dark:bg-indigo-400/20 dark:text-indigo-100">
                          {formatHours(client.totalHours)}
                        </span>
                      </td>
                    </tr>
                    {client.projects.map((project) => (
                      <tr key={project.projectId} className="border-t border-gray-100 bg-indigo-50 dark:border-gray-700/80 dark:bg-[#2b355f]">
                        <td className="px-4 py-3 pl-9 font-medium text-gray-800 dark:text-gray-100">
                          {project.name}
                          {project.remark && <p className="mt-1 text-xs font-normal text-amber-700 dark:text-amber-300">Remark: {project.remark}</p>}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-200"><span className="font-semibold text-indigo-900 dark:text-indigo-100">{formatHours(project.totalHours)}</span></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300">
            <FiUser size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 break-all">
              {detail.user.username}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {detail.user.department || "-"} • ชั่วโมงรวม {formatHours(detail.totalHours)}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-indigo-300 dark:hover:border-indigo-700"
        >
          <FiArrowLeft />
          ปิด
        </button>
      </div>

      <div className="mt-6 overflow-hidden rounded-3xl border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto pb-5">
          <table className="min-w-[1100px] w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/60">
              <tr>
                <th className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-900/60 px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200 min-w-[320px]">
                  ลูกค้า / Project
                </th>
                {days.map((day) => (
                  <th
                    key={formatDateKey(day)}
                    className="px-3 py-3 text-center font-semibold text-gray-700 dark:text-gray-200"
                  >
                    <div>{formatWeekdayLabel(day)}</div>
                    <div className="mt-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                      {formatShortDateLabel(day)}
                    </div>
                  </th>
                ))}
                <th className="px-4 py-3 text-center font-semibold text-gray-700 dark:text-gray-200">
                  ชั่วโมงรวม
                </th>
              </tr>
            </thead>
            <tbody>
              {detail.clients.map((client) => (
                <React.Fragment key={client.clientId}>
                  <tr className="border-t border-gray-100 bg-indigo-100 dark:border-gray-700/80 dark:bg-[#3a447b]">
                    <td className="sticky left-0 z-[1] bg-indigo-100 px-4 py-3 font-semibold text-gray-900 dark:bg-[#3a447b] dark:text-gray-100">
                      {client.clientName}
                    </td>
                    {days.map((day) => (
                      <td
                        key={`${client.clientId}:${formatDateKey(day)}`}
                        className="px-3 py-3 text-center text-gray-400"
                      >
                        -
                      </td>
                    ))}
                    <td className="px-4 py-3 text-center font-semibold text-gray-900 dark:text-gray-100">
                      <span className="inline-flex min-w-9 items-center justify-center rounded-full bg-indigo-200/80 px-3 py-1 text-indigo-950 dark:bg-indigo-400/20 dark:text-indigo-100">
                        {formatHours(client.totalHours)}
                      </span>
                    </td>
                  </tr>
                  {client.projects.map((project, projectIndex) => (
                    <tr key={project.projectId} className={`border-t border-gray-100 dark:border-gray-700/80 ${projectIndex % 2 === 0 ? "bg-slate-50 dark:bg-[#202a40]" : "bg-white dark:bg-[#182236]"}`}>
                      <td className={`sticky left-0 z-[1] px-4 py-3 pl-9 font-medium text-gray-800 dark:text-gray-100 ${projectIndex % 2 === 0 ? "bg-slate-50 dark:bg-[#202a40]" : "bg-white dark:bg-[#182236]"}`}>
                        {project.name}
                        {project.remark && <p className="mt-1 text-xs font-normal text-amber-700 dark:text-amber-300">Remark: {project.remark}</p>}
                      </td>
                      {days.map((day) => {
                        const dateKey = formatDateKey(day);
                        const hours = project.dailyHours?.[dateKey] || 0;
                        return <td key={`${project.projectId}:${dateKey}`} className="px-3 py-3 text-center text-gray-700 dark:text-gray-200">{hours > 0 ? formatHours(hours) : "-"}</td>;
                      })}
                      <td className="px-4 py-3 text-center font-semibold text-indigo-900 dark:text-indigo-100">{formatHours(project.totalHours)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-indigo-200 bg-indigo-100 dark:border-indigo-900/70 dark:bg-[#3a447b]">
              <tr>
                <td className="sticky left-0 z-[1] bg-indigo-100 px-4 py-3 font-semibold text-indigo-900 dark:bg-[#3a447b] dark:text-indigo-100">
                  รวมรายวัน
                </td>
                {days.map((day) => {
                  const dateKey = formatDateKey(day);
                  return (
                    <td key={dateKey} className="px-3 py-3 text-center font-semibold text-indigo-900 dark:text-indigo-100">
                      {formatHours(dailyTotals[dateKey])}
                    </td>
                  );
                })}
                <td className="px-4 py-3 text-center font-bold text-indigo-950 dark:text-indigo-50">
                  {formatHours(totalHours)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EmployeeTimesheetDetail;
